import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
// @ts-ignore: allow side-effect CSS import without module declarations
import "react-toastify/dist/ReactToastify.css";
import api from "src/features/auth/axiosConfig";
import CommonTable, { HeadCell } from "src/business-types/grocery/components/CommonTable";
import { PageTitle } from "src/shared/PageTitle";
import ReportsMenuTab from "./ReportsMenuTab";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { pageTitle } from "src/shared/utils/brand";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

// API Response Interface - Use this instead of DailyReportRow
export interface ReportItem {
  _id: string;
  orgId: string;
  reportType: string;
  reportDate: string;
  fileName: string;
  fileKey: string;
  fileUrl: string;
  createdAt: string;
  createdBy: string;
}
export interface ReportsApiResponse {
  count: number;
  results: ReportItem[];
}

export interface ReportTableRow {
  id: string;
  sno: number;
  // reportDate: string;
  // reportPeriod: string;
  fileName: string;
  createdAt: string;
  actions: React.ReactNode;
}

// Helper function to get local date using 'now' approach
const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DailyReport: React.FC = () => {
  const [generatedReports, setGeneratedReports] = useState<any[]>([]);
  const [reportData, setReportData] = useState<ReportTableRow[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dateFilter, setDateFilter] = useState<string>("Today");

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState<{ id: string; fileName: string } | null>(null);

  // Initialize with local date using 'now' approach
  const now = new Date();
  const today = getLocalDateString(now);
  const [customStartDate, setCustomStartDate] = useState<string>(today);
  const [customEndDate, setCustomEndDate] = useState<string>(today);
  const { sidebarThemeClass } = useUserTheme();

  useEffect(() => {
    document.title = pageTitle("Sales Report");
  }, []);

  // Get report name based on filter
  const getReportName = (): string => {
    switch (dateFilter) {
      case "Today":
        return "Today's Report";
      case "Yesterday":
        return "Yesterday's Report";
      case "LastWeek":
        return "Last 7 Days Report";
      case "LastMonth":
        return "Last 30 Days Report";
      case "CustomizeDate":
        return `Custom Report (${customStartDate} to ${customEndDate})`;
      default:
        return "Report";
    }
  };

  const getDateDisplayString = (): string => {
    const now = new Date();

    switch (dateFilter) {
      case "Today":
        return getLocalDateString(now);
      case "Yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return getLocalDateString(yesterday);
      case "LastWeek":
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return `${getLocalDateString(lastWeek)} to ${getLocalDateString(now)}`;
      case "LastMonth":
        const lastMonth = new Date(now);
        lastMonth.setDate(lastMonth.getDate() - 30);
        return `${getLocalDateString(lastMonth)} to ${getLocalDateString(now)}`;
      case "CustomizeDate":
        return `${customStartDate} to ${customEndDate}`;
      default:
        return getLocalDateString(now);
    }
  };

  const handleGenerateReport = async () => {
    setIsGenerating(true);

    try {
      const queryParams = new URLSearchParams();

      if (dateFilter === "CustomizeDate") {
        queryParams.append("start_date", customStartDate);
        queryParams.append("end_date", customEndDate);
      } else {
        queryParams.append("date", dateFilter.toLowerCase());
      }

      queryParams.append("export_type", "pdf");

      const response = await api.get(`/app/pos/v2/report?${queryParams.toString()}`, {
        responseType: "arraybuffer",
      });

      const file = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = URL.createObjectURL(file);
      const reportName = getReportName();
      const dateString = getDateDisplayString();
      const reportId = `report-${Date.now()}`;

      const newGeneratedReport = {
        id: reportId,
        url,
        reportName,
        date: dateString,
      };

      setGeneratedReports((prev) => [newGeneratedReport, ...prev]);

      // After generating report, refresh the table data
      await genrateTableCollection();

      toast.success("Report generated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Report API error:", err);
      toast.error("Failed to generate report. Please try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const formatToIST = (dateString: string) => {
    const hasTimezone = /Z|[+-]\d{2}:\d{2}$/.test(dateString);
    const utcDate = new Date(hasTimezone ? dateString : dateString + "Z");

    return utcDate.toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const genrateTableCollection = async () => {
    try {
      const response = await api.get<ReportsApiResponse>(`/app/pos/v2/get_reports?report_type=daily`);

      if (response.data?.results) {
        const tableData: ReportTableRow[] = response.data.results.map((report: ReportItem, index: number) => ({
          id: report._id,
          sno: index + 1,
          fileName: report.fileName,
          createdAt: formatToIST(report.createdAt),
          actions: renderActions(report._id, report.fileName, report.fileUrl),
        }));

        setReportData(tableData);
      }
    } catch (err) {
      console.error("Error generating table collection:", err);
      toast.error("Failed to load reports", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (reportId: string, fileName: string) => {
    setReportToDelete({ id: reportId, fileName });
    setIsDeleteModalOpen(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReportToDelete(null);
  };

  // Handle actual delete after confirmation
  const handleConfirmDelete = async () => {
    if (!reportToDelete?.id) {
      toast.error("Invalid report ID", { position: "top-right", autoClose: 3000 });
      closeDeleteModal();
      return;
    }

    try {
      await api.delete(`/app/pos/v2/get_reports/${reportToDelete.id}`);

      toast.success("Report deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      await genrateTableCollection();
      closeDeleteModal();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete report", {
        position: "top-right",
        autoClose: 3000,
      });
      closeDeleteModal();
    }
  };

  useEffect(() => {
    genrateTableCollection();
  }, []);

  const handleViewReport = async (fileUrl: string) => {
    if (!fileUrl) {
      toast.error("Report not found", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Open a new tab immediately to prevent browser popup blockers
    const newTab = window.open("", "_blank");
    if (!newTab) {
      toast.error("Popup blocked! Please allow popups for this site.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Show a modern, styled loading indicator in the new tab
    newTab.document.write(`
      <html>
        <head>
          <title>Loading Report PDF...</title>
          <style>
            body {
              margin: 0;
              padding: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
              color: #333333;
            }
            .loader-container {
              background: rgba(255, 255, 255, 0.85);
              padding: 30px 50px;
              border-radius: 16px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
              backdrop-filter: blur(10px);
              border: 1px solid rgba(255, 255, 255, 0.2);
              text-align: center;
              max-width: 400px;
              width: 80%;
            }
            .spinner {
              border: 4px solid rgba(0, 0, 0, 0.05);
              border-top: 4px solid #3b82f6;
              border-radius: 50%;
              width: 45px;
              height: 45px;
              animation: spin 1s linear infinite;
              margin: 0 auto 20px;
            }
            .title {
              font-size: 1.1rem;
              font-weight: 600;
              margin: 0 0 8px 0;
              color: #1f2937;
            }
            .subtitle {
              font-size: 0.875rem;
              color: #6b7280;
              margin: 0;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="loader-container">
            <div class="spinner"></div>
            <div class="title">Preparing Report</div>
            <div class="subtitle">Fetching and rendering PDF document...</div>
          </div>
        </body>
      </html>
    `);
    newTab.document.close();

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const blob = await response.blob();
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(pdfBlob);
      newTab.location.replace(blobUrl);
    } catch (err) {
      console.error("Error viewing PDF:", err);
      newTab.close();
      toast.error("Failed to load PDF inline. Attempting standard download...", {
        position: "top-right",
        autoClose: 3000,
      });
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const renderActions = (reportId: string, fileName: string, fileUrl: string) => {
    return (
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => handleViewReport(fileUrl)}
          className="h-8 px-3 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          View
        </button>
        <button
          onClick={() => openDeleteModal(reportId, fileName)}
          className="h-8 px-3 text-sm font-medium bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Delete
        </button>
      </div>
    );
  };

  const headCells: HeadCell<ReportTableRow>[] = [
    { id: "sno", label: "S.No", sorting: false },
    { id: "fileName", label: "File Name", align: "center" },
    { id: "createdAt", label: "Generated on", align: "center", sorting: true },
    { id: "actions", label: "Actions", align: "center", sorting: false },
  ];

  const showCustomDateRange = dateFilter === "CustomizeDate";

  useEffect(() => {
    if (dateFilter === "CustomizeDate") {
      if (new Date(customStartDate) > new Date(customEndDate)) {
        toast.error("Start date cannot be later than end date", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  }, [customStartDate, customEndDate, dateFilter]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      generatedReports.forEach((report) => {
        if (report.url) {
          URL.revokeObjectURL(report.url);
        }
      });
    };
  }, [generatedReports]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden p-1.5">
      <div className={`w-full h-full flex flex-col shadow-inner rounded-lg overflow-auto ${sidebarThemeClass}`}>
        <PageTitle title="- Sales Report" />
        <ReportsMenuTab />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2 mt-2">
          <h2 className="text-xl font-semibold ml-6 ">Reports</h2>
          <div className="flex flex-wrap items-center gap-3 mr-6">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="options" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Choose an option:
              </label>

              <select
                id="options"
                name="options"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="LastWeek">Last 7 Days</option>
                <option value="LastMonth">Last 30 Days</option>
                <option value="CustomizeDate">Custom Date Range</option>
              </select>

              {/* Custom Date Range Inputs */}
              {showCustomDateRange && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    max={customEndDate}
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min={customStartDate}
                  />
                </div>
              )}
            </div>

            {/* Generate Report Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleGenerateReport}
                disabled={isGenerating}
                className={`h-9 rounded-md px-4 text-sm font-medium text-white ${
                  isGenerating ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                } transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
              >
                {isGenerating ? "Generating..." : "Generate Report"}
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && reportToDelete && (
          <ModalWrapper title="Confirm Delete" onClose={closeDeleteModal} showBackButton={false} width="max-w-md" positionClass="top-0 left-0 right-0 mt-10 h-full">
            <div className="text-center">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure you want to delete this report?</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Report: <span className="font-medium text-gray-700">{reportToDelete.fileName}</span>
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </ModalWrapper>
        )}

        {/* Table */}
        <div className="w-full overflow-y-auto pt-2 px-4 flex-1">
          <CommonTable<ReportTableRow>
            data={reportData}
            headCells={headCells}
            title="Generated Reports"
            selectable={false}
            showToolbar={false}
            pagenation={true}
            pageSize={10}
            denseDefault={false}
            className="w-full shadow-inner rounded-lg bg-white p-2"
          />
        </div>

        {/* Toast */}
        <ToastContainer position="top-right" autoClose={2000} />
      </div>
    </div>
  );
};

export default DailyReport;
